/**
 * Signals to Prerender.io that the page's async-loaded content (Contentful
 * data, meta tags, JSON-LD, etc.) has resolved and the page is ready to be
 * snapshotted. `window.prerenderReady` starts `false` (set inline in
 * public/index.html) so Prerender.io always waits by default.
 *
 * Several independent pieces of data feed the page's full JSON-LD output
 * (the page's own Metadata, the sitewide siteConfig fetched once in
 * Root.tsx, and OrganizationSchema's own footer fetch), each resolving on
 * its own schedule. Rather than flipping the flag from a single one of
 * these, every dependency reports in via `markReady(key)` once it has
 * settled (successfully or not), and the flag only flips once all of them
 * have - or a bounded safety-net timeout elapses, whichever comes first.
 * This keeps the signal deterministic without ever blocking indefinitely
 * on a single stalled fetch, and stays well under Prerender.io's own
 * 20-second hard render timeout so we're the ones deciding when to give
 * up, not relying on their fallback by default.
 * See: https://docs.prerender.io/docs/best-practices-for-crawler-ready-pages
 * See: https://docs.prerender.io/docs/25-empty-or-partially-rendered-pages
 */

export type PrerenderReadyKey =
  | "siteConfig"
  | "organizationFooter"
  | "metadata";

const SAFETY_NET_TIMEOUT_MS = 5000;

const pending = new Set<PrerenderReadyKey>([
  "siteConfig",
  "organizationFooter",
  "metadata",
]);

let fired = false;

const fire = () => {
  if (fired) {
    return;
  }
  fired = true;
  if (typeof window !== "undefined") {
    (window as any).prerenderReady = true;
  }
};

// Marks one dependency as settled. Safe to call multiple times for the
// same key (e.g. on a locale switch re-triggering a fetch) - clearing an
// already-cleared key is a no-op.
export const markReady = (key: PrerenderReadyKey) => {
  pending.delete(key);
  if (pending.size === 0) {
    fire();
  }
};

if (typeof window !== "undefined") {
  window.setTimeout(fire, SAFETY_NET_TIMEOUT_MS);
}
